import React, { createContext, useEffect, useState, useContext } from 'react'
import axios from 'axios'
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { ErrorOutline } from '@mui/icons-material'
import './App.css'
import AutoForm from './AutoForm'
import AutoFormSkeleton from './AutoFormSkeleton'
import constants from './util/constants'
import ModalSendEmail from './ModalSendEmail'
import { config } from './constants'
import AccountLogin from './AccountLogin'
import ReCAPTCHA from 'react-google-recaptcha'
import { ConfigContext } from './App'

const FormContext = createContext()
const context = { fields: {}, state: {}, displayName: 'WebinarFormContext' }

const getFormattedDate = (times) => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ]

  let date = new Date(times.startTime)
  let hours = date.getHours()
  let endHours = new Date(times.endTime).getHours()

  let minutes = date.getMinutes()
  minutes = minutes < 10 ? '0' + minutes : minutes
  let endMinutes = date.getMinutes()
  endMinutes = endMinutes < 10 ? '0' + endMinutes : endMinutes
  let timeZone = -date.getTimezoneOffset() / 60
  timeZone = timeZone >= 0 ? '+' + timeZone : timeZone

  let formattedDate =
    days[date.getDay()] +
    ', ' +
    months[date.getMonth()] +
    ' ' +
    date.getDate() +
    ', ' +
    date.getFullYear() +
    ' at ' +
    hours +
    ':' +
    minutes
  formattedDate += ' - ' + endHours + ':' + endMinutes + ' '
  formattedDate += ' UTC' + timeZone

  return formattedDate
}

const DateView = (props) => {
  const { startTime, endTime } = props.item
  return <Typography>{getFormattedDate({ startTime, endTime })}</Typography>
}

const JoinWebinar = ({ product, productContent }) => {
  const navigate = useNavigate()
  const [info, setInfo] = useState({
    submitting: false,
    futureWebinars: [],
    registerError: '',
    webinarDate: '',
    cadSkillLevel: '',
    jewelryBusiness: '',
    checked: false,
    errors: '',
    webinarKey: '',
    disclaimer: undefined,
    sendEmailOpen: false
  })
  const [reCaptchaToken, setReCaptchaToken] = useState('')
  const [loading, setLoading] = useState(true)
  const appContext = useContext(ConfigContext)

  useEffect(() => {
    let { registerError } = info
    const getInfo = async () => {
      let newInfo = { ...info }
      let fields
      try {
        const webinarsInfo = await axios.get(`${config.BASE_URL}/zoom/webinars`, { params: { product } })
        newInfo = {
          ...newInfo,
          futureWebinars: webinarsInfo.data.times,
          webinarDate: '0',
          webinarKey: webinarsInfo.data.times[0].webinarKey
        }
        const webinarsFields = await axios.get(`${config.BASE_URL}/zoom/webinars/fields`, { params: { product } })
        fields = setStateFromFields(webinarsFields.data)
      } catch (error) {
        console.error(error.message ?? error)
        registerError = 'There is currently no available webinars, please try again later.'
      }
      setInfo({ ...newInfo, ...fields, registerError })
      setLoading(false)
    }
    getInfo()
  }, [])

  const moveElementInArray = (arr, fromIndex, toIndex) => {
    let newArray = [...arr]
    let element = newArray[fromIndex]
    newArray.splice(fromIndex, 1)
    newArray.splice(toIndex, 0, element)
    return newArray
  }

  const setStateFromFields = (data) => {
    let fields = constants.requiredFields.concat(data.questions)
    let cityIndex = fields.findIndex((field) => field.field_name === 'city')
    if (cityIndex > -1) {
      let stateIndex = fields.findIndex((field) => field.field_name === 'state')
      fields = moveElementInArray(fields, cityIndex, stateIndex)
    }
    let questions = data.custom_questions ? data.custom_questions : []

    context['fields'] = fields
    context['questions'] = questions
    context['state'] = {}
    if (data.questions.length > 0) {
      context.state['responses'] = []
    }

    let errors = {}
    let disclaimer = undefined
    fields.forEach((element) => {
      if (constants.dropdownFields.includes(element.field_name)) {
        typeof constants[element.field_name][0] === 'object'
          ? (element.options = constants[element.field_name])
          : (element.answers = constants[element.field_name])
      }
      if (element.required) {
        errors[element.field_name] = false
      }
    })
    questions.forEach((element) => {
      if (element.required) {
        errors[element.title] = false
      }
    })

    return { fields, questions, errors, disclaimer }
  }

  const handleWebinarDate = (event) => {
    setInfo({ ...info, webinarKey: event.target.value })
  }

  const handleChange = (event) => {
    context.state['disclaimer'] = event.target.checked
    if (event.target.checked) {
      let newErrors = { ...info.errors }
      newErrors['disclaimer'] = false
      setInfo({ ...info, errors: newErrors })
    }
  }

  const onChangeReCaptcha = (token) => {
    setReCaptchaToken(token)
  }

  const closeSendEmail = () => {
    setInfo({ ...info, sendEmailOpen: false })
  }

  const validateEmail = (email) => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return !re.test(email.toLowerCase())
  }

  const validateForm = () => {
    setInfo({ ...info, registerError: '' })
    let hasErrors = false
    let newErrors = {}
    for (let key in info.errors) {
      newErrors[key] = false
    }
    Object.keys(newErrors).forEach((field) => {
      if (context.state[field] === '' || context.state[field] === false) {
        newErrors[field] = true
        hasErrors = true
      } else if (field === 'email' && validateEmail(context.state[field])) {
        newErrors[field] = true
        hasErrors = true
      } else {
        let found = context.state.responses.find((element) => element.title + '' === field)
        if (found) {
          if (found.responseText || found.responseText === '') {
            found.value = found.responseText
          }
          if (found.value === '') {
            newErrors[field] = true
            hasErrors = true
          }
        }
      }
    })
    setInfo({ ...info, errors: newErrors })

    return hasErrors
  }

  const parsePhone = (phoneNumberString, country) => {
    if (country === 'US') {
      let cleaned = ('' + phoneNumberString).replace(/\D/g, '')
      let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
      if (match) {
        return '(' + match[1] + ') ' + match[2] + '-' + match[3]
      }
      throw 'Invalid US number'
    } else {
      return phoneNumberString
    }
  }

  const onSubmit = async () => {
    let hasErrors = validateForm()
    let { registerError, sendEmailOpen } = info
    if (registerError) {
      return
    }
    if (hasErrors) {
      return
    }
    const body = { ...context.state }
    body.custom_questions = body.responses
    delete body.responses
    try {
      body.phone = parsePhone(body.phone, body.country)
    } catch (err) {
      setInfo({ ...info, errors: { phone: true } })
      return
    }
    setInfo({ ...info, submitting: true })
    try {
      const response = await axios.post(`${config.BASE_URL}/zoom/register/${info.webinarKey}`, body, {
        params: { product: product }
      })
      if (response.data && response.data.status === 'BlockedUser') {
        sendEmailOpen = true
      } else {
        let defaultPath = product === 'countersketch' ? '/countersketch-webinar' : '/matrixgold-webinar'
        navigate(defaultPath + '/confirmation')
      }
    } catch (error) {
      if (error.response && error.response.data) {
        registerError = error.response.data.message
      } else {
        registerError = 'Error, try again later'
      }
    }
    setInfo({ ...info, submitting: false, registerError, sendEmailOpen })
  }

  const { webinarKey, futureWebinars, errors, registerError, disclaimer, fields, sendEmailOpen, submitting } = info
  return (
    <>
      <ModalSendEmail open={sendEmailOpen} closeSendEmail={closeSendEmail} />
      <main>
        <div className='title' style={{ backgroundColor: productContent.titleBackgroundColor }}>
          <img src={productContent.background} className='mg-img-absolute' alt={product} />
        </div>
        <div className='two-column-wrapper'>
          <section>
            <div className='mg-live-session'>
              <img className='mg-logo' src={productContent.logo} alt={product} />
              <br></br>
            </div>
            <p>{productContent.description1}</p>
            <p>{productContent.description2}</p>
            <video width='100%' height='auto' poster={productContent.poster} controls>
              <source src={productContent.video} />
            </video>
          </section>
          <section>
            <div className='form-wrapper'>
              <h3>Subscribe to our Webinar</h3>
              <form noValidate autoComplete='off' style={{ margin: '1rem 0 2rem' }}>
                <p>This webinar is offered several times. Select the date and time that works best for you.</p>
                <FormControl required error={errors.webinarDate} variant='filled' style={{ marginBottom: '2rem' }}>
                  <InputLabel>Webinar Date</InputLabel>
                  <Select value={webinarKey} onChange={handleWebinarDate}>
                    {futureWebinars.map((row) => {
                      return (
                        <MenuItem value={row.webinarKey} key={row.webinarKey}>
                          <DateView item={row} />
                        </MenuItem>
                      )
                    })}
                  </Select>
                </FormControl>
                {fields && (
                  <FormContext.Provider value={context}>
                    <AccountLogin />
                    <AutoForm fields={fields} errors={errors} formContext={FormContext} />
                  </FormContext.Provider>
                )}
                {loading && <AutoFormSkeleton />}
                {disclaimer && (
                  <FormControlLabel
                    style={{ marginTop: 8 }}
                    control={
                      <Checkbox
                        className={errors['disclaimer'] ? 'checkbox-error' : undefined}
                        defaultChecked={false}
                        onChange={handleChange}
                        color='primary'
                      />
                    }
                    label={disclaimer.message}
                  />
                )}
              </form>
              <div
                data-testid='recaptcha'
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '1.5rem' }}
              >
                <ReCAPTCHA
                  sitekey={appContext.params.recaptchaApiKey || 'undefined'}
                  onChange={onChangeReCaptcha}
                  theme='dark'
                />
              </div>
              <Button
                fullWidth
                color='primary'
                size='large'
                disabled={submitting || !reCaptchaToken}
                onClick={onSubmit}
                style={{ backgroundColor: productContent.buttonColor }}
              >
                Join Webinar
              </Button>
              {registerError !== '' && (
                <div className='form-error'>
                  <ErrorOutline />
                  <p>{registerError}</p>
                </div>
              )}
            </div>
          </section>
        </div>
        <div className='meet-host'>
          <h3>Meet your Host</h3>
          <div className='image-frame'>
            <img src={productContent.host} width='100%' alt='Webinar Host' />
          </div>
          <p>{productContent.hostDescription}</p>
        </div>
      </main>
    </>
  )
}

export default JoinWebinar
